import { useState } from 'react';
import { CheckCircle, Loader2, Lock, Mail, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { saveCheckoutResume } from '@/pages/order/checkoutResume';

export const CheckoutAuthGate = ({ shippingAddress }) => {
  const { requestMagicLink, passwordLogin } = useAuth();
  const [tab, setTab] = useState('magic');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const handleMagicLink = async (event) => {
    event.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setError(null);
    try {
      saveCheckoutResume({
        return_to: '/order/checkout',
        shipping_address: shippingAddress,
        requested_email: email.trim(),
        awaiting_magic_link: true,
        resumed_via: null,
      });
      await requestMagicLink(email.trim());
      setSent(true);
      toast.success('Magic link sent. Return here after you sign in.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send magic link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordLogin = async (event) => {
    event.preventDefault();
    if (!email.trim() || !password) return;
    setLoading(true);
    setError(null);
    try {
      await passwordLogin(email.trim(), password);
      toast.success('Signed in. You can continue checkout now.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid email or password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="bg-white border-stone/50" data-testid="checkout-auth-gate">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start gap-3">
          <div className="rounded-full p-2" style={{ background: 'rgba(14,107,93,0.08)' }}>
            <ShieldCheck className="h-4 w-4" style={{ color: '#0E6B5D' }} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-ink" data-testid="checkout-auth-gate-title">Sign in to continue checkout</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Checkout now requires a customer account before we create your order or initialize payment.
            </p>
          </div>
        </div>

        {!sent ? (
          <Tabs value={tab} onValueChange={(value) => { setTab(value); setError(null); }} className="w-full">
            <TabsList className="grid w-full grid-cols-2" data-testid="checkout-auth-tabs">
              <TabsTrigger value="magic" data-testid="checkout-auth-magic-tab"><Mail className="h-3.5 w-3.5 mr-1" /> Magic Link</TabsTrigger>
              <TabsTrigger value="password" data-testid="checkout-auth-password-tab"><Lock className="h-3.5 w-3.5 mr-1" /> Password</TabsTrigger>
            </TabsList>

            <TabsContent value="magic" className="pt-4">
              <form onSubmit={handleMagicLink} className="space-y-4" data-testid="checkout-auth-magic-form">
                <div>
                  <Label htmlFor="checkout-auth-magic-email" className="text-xs">Email Address</Label>
                  <Input
                    id="checkout-auth-magic-email"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="you@example.com"
                    className="mt-1"
                    required
                    data-testid="checkout-auth-magic-email-input"
                  />
                </div>
                {error && (
                  <Alert variant="destructive" data-testid="checkout-auth-error-alert">
                    <AlertDescription className="text-xs">{error}</AlertDescription>
                  </Alert>
                )}
                <Button type="submit" disabled={loading} className="gap-2 rounded-[12px] h-11" data-testid="checkout-auth-magic-submit-button">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                  {loading ? 'Sending...' : 'Send Magic Link'}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="password" className="pt-4">
              <form onSubmit={handlePasswordLogin} className="space-y-4" data-testid="checkout-auth-password-form">
                <div>
                  <Label htmlFor="checkout-auth-password-email" className="text-xs">Email Address</Label>
                  <Input
                    id="checkout-auth-password-email"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="you@example.com"
                    className="mt-1"
                    required
                    data-testid="checkout-auth-password-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="checkout-auth-password" className="text-xs">Password</Label>
                  <Input
                    id="checkout-auth-password"
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="Enter your password"
                    className="mt-1"
                    required
                    data-testid="checkout-auth-password-input"
                  />
                </div>
                {error && (
                  <Alert variant="destructive" data-testid="checkout-auth-password-error-alert">
                    <AlertDescription className="text-xs">{error}</AlertDescription>
                  </Alert>
                )}
                <Button type="submit" disabled={loading} className="gap-2 rounded-[12px] h-11" data-testid="checkout-auth-password-submit-button">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        ) : (
          <div className="rounded-2xl border border-stone/40 bg-stone-50 p-4 space-y-3" data-testid="checkout-auth-magic-sent-state">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4" style={{ color: '#0E6B5D' }} />
              <p className="text-sm font-semibold text-ink">Check your email</p>
            </div>
            <p className="text-xs text-muted-foreground">
              We sent a sign-in link to <strong>{email}</strong>. Finish signing in, then return to this checkout to continue.
            </p>
            <Button variant="outline" size="sm" onClick={() => { setSent(false); setError(null); }} data-testid="checkout-auth-try-another-email-button">
              Try a Different Email
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};